/**
 * localStorage-backed store for the session (pattern from cgt-frontend
 * src/lib/authToken.ts). Framework-free so api helpers can read the token
 * without importing React. Every key lives under `ss.auth.*`.
 */

const ACCESS_KEY = 'ss.auth.accessToken'
const REFRESH_KEY = 'ss.auth.refreshToken'
const SESSION_KEY = 'ss.auth.session'

export interface StoredAuth {
  accessToken: string
  refreshToken?: string
}

export type PrincipalKind = 'AGENT' | 'CUSTOMER'

/** The signed-in principal, as returned by `GET /auth/me`. */
export interface AuthSession {
  id: string
  name: string
  email: string
  kind: PrincipalKind
  isSuperAdmin: boolean
  permissions: string[]
  role?: string | null
  tier?: number | null
  teamName?: string | null
  /** Job title, e.g. `Tier 2 · Validated env.` (agents) or `QA Release Officer` (customers). */
  title?: string | null
  customerId: string | null
  contactId: string | null
  /** `null` = unrestricted. */
  scope: { productIds: string[] | null; customerIds: string[] | null }
}

export function getAccessToken(): string | null {
  return localStorage.getItem(ACCESS_KEY)
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_KEY)
}

export function setAuth({ accessToken, refreshToken }: StoredAuth): void {
  localStorage.setItem(ACCESS_KEY, accessToken)
  if (refreshToken) localStorage.setItem(REFRESH_KEY, refreshToken)
}

export function getSession(): AuthSession | null {
  const raw = localStorage.getItem(SESSION_KEY)
  if (!raw) return null
  try {
    return JSON.parse(raw) as AuthSession
  } catch {
    return null
  }
}

export function setSession(session: AuthSession): void {
  localStorage.setItem(SESSION_KEY, JSON.stringify(session))
}

export function clearAuth(): void {
  localStorage.removeItem(ACCESS_KEY)
  localStorage.removeItem(REFRESH_KEY)
  localStorage.removeItem(SESSION_KEY)
}

export function isAuthenticated(): boolean {
  return Boolean(getAccessToken())
}
